import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useMe } from '@/auth/useAuth';
import { api } from '@/lib/api';
import { useWsEvent } from '@/lib/useWsEvent';
import { listFamilyShareLinks, type FamilyShareLink } from '@/pages/learn/playground/sharingApi';
import type { MyClassesResponse } from '@/pages/portal/myClasses';
import type { AutoTopupResponse } from '@/pages/portal/walletTypes';

interface ApprovalLite {
  status: string;
}

interface WalletLite {
  stars_balance: number;
}

export function usePortalPendingCount() {
  const me = useMe();
  const queryClient = useQueryClient();
  const familyId = me.data?.kind === 'user' ? me.data.family_id : null;

  const approvals = useQuery<ApprovalLite[]>({
    queryKey: ['approvals', familyId],
    queryFn: () => api<ApprovalLite[]>(`/families/${familyId}/approvals`),
    enabled: !!familyId,
  });
  const shareLinks = useQuery<FamilyShareLink[]>({
    queryKey: ['share-requests', familyId],
    queryFn: () => listFamilyShareLinks(familyId!),
    enabled: !!familyId,
  });
  const classes = useQuery<MyClassesResponse>({
    queryKey: ['families', familyId, 'my-classes'],
    queryFn: () => api<MyClassesResponse>(`/families/${familyId}/my-classes`),
    enabled: !!familyId,
  });
  const wallet = useQuery<WalletLite>({
    queryKey: ['wallet', familyId],
    queryFn: () => api<WalletLite>(`/families/${familyId}/wallet`),
    enabled: !!familyId,
  });
  const autoTopup = useQuery<AutoTopupResponse>({
    queryKey: ['wallet', familyId, 'auto-topup'],
    queryFn: () => api<AutoTopupResponse>(`/families/${familyId}/wallet/auto-topup`),
    enabled: !!familyId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['approvals', familyId] });
    queryClient.invalidateQueries({ queryKey: ['share-requests', familyId] });
    queryClient.invalidateQueries({ queryKey: ['families', familyId, 'my-classes'] });
    queryClient.invalidateQueries({ queryKey: ['wallet', familyId] });
  };
  useWsEvent('approval.new', invalidate, [familyId]);
  useWsEvent('approval.resolved', invalidate, [familyId]);
  useWsEvent('project.share_link_created', invalidate, [familyId]);

  const config = autoTopup.data?.config;
  const attempts = autoTopup.data?.recent_attempts ?? [];
  const lowBalance = Boolean(
    wallet.data && config && wallet.data.stars_balance <= config.threshold_stars,
  );
  const failedAutoTopup = attempts.some((attempt) => attempt.status === 'failed');
  const cappedAutoTopup = attempts.some(
    (attempt) =>
      attempt.status === 'skipped' && /cap/i.test(attempt.reason ?? attempt.skip_reason ?? ''),
  );
  const pausedAutoTopup = Boolean(
    config && !config.enabled && config.consecutive_failures >= config.failure_threshold,
  );

  return (
    (approvals.data?.filter((approval) => approval.status === 'pending').length ?? 0) +
    (shareLinks.data?.filter((shareLink) => shareLink.status === 'pending').length ?? 0) +
    (classes.data?.pending_orders.length ?? 0) +
    Number(lowBalance) +
    Number(failedAutoTopup) +
    Number(cappedAutoTopup) +
    Number(pausedAutoTopup)
  );
}
